import { c as _c } from "react/compiler-runtime";
import type { TFunction } from "i18next";
import { ChevronLeft, Sparkles } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { BUYER_ASSISTANT_MAX_LENGTH, BuyerAssistantError, useAskBuyerAssistant, useBuyerAssistantHistory, useClearBuyerAssistant } from "@/api/use-buyer-assistant";
import { SendIcon, XIcon } from "@/components/icons";
import { SignInCard } from "@/components/profile/sign-in-card";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { useProfile, useWishlist } from "@/store";
import { AssistantMessage } from "./assistant-message";
import { isCompactViewport } from "./format";

// Панель помощника: шапка, лента на #F8F8F8 (как тред «Чатов»), поле ввода.
// На десктопе — карточка 400×600 у правого нижнего угла, на телефоне — на весь
// экран поверх таб-бара. Слой z-60 — выше плашки cookie (z-50): на десктопе
// она шириной 768px по центру и иначе закрывала поле ввода панели. Гость видит вступление и кнопку входа: без входа
// помощник не знает покупок и избранного.
//
// Вход — прямо в панели, той же карточкой SignInCard, что в «Чатах» и
// профиле: покупатель остаётся на своей странице, а после кода из СМС панель
// открыта и сразу готова к вопросу. Раньше «Войти» уводило на /profile, и
// вернуться к товару и помощнику было нечем.

const SUGGESTIONS = ["trends", "look", "handmade", "gift"] as const;

// Текст ошибки — по коду из use-buyer-assistant; чужая ошибка — общий текст.
const errorText = (t: TFunction, error: unknown): string => t(`assistant.errors.${error instanceof BuyerAssistantError ? error.code : "generic"}`);
export function AssistantPanel(t0) {
  const $ = _c(99);
  const {
    onClose
  } = t0;
  const {
    t
  } = useTranslation();
  const token = useProfile(_temp);
  const user = useProfile(_temp2);
  const hydrateMe = useProfile(_temp3);
  const signedIn = Boolean(token && user);
  const syncWishlist = useWishlist(_temp4);
  const [signingIn, setSigningIn] = useState(false);
  const showSignIn = signingIn && !signedIn;
  let t1;
  let t2;
  if ($[0] !== hydrateMe || $[1] !== token || $[2] !== user) {
    t1 = () => {
      if (token && !user) {
        hydrateMe().catch(_temp5);
      }
    };
    t2 = [hydrateMe, token, user];
    $[0] = hydrateMe;
    $[1] = token;
    $[2] = user;
    $[3] = t1;
    $[4] = t2;
  } else {
    t1 = $[3];
    t2 = $[4];
  }
  useEffect(t1, t2);
  const history = useBuyerAssistantHistory(true);
  const ask = useAskBuyerAssistant();
  const clear = useClearBuyerAssistant();
  let t3;
  if ($[5] !== history || $[6] !== signedIn) {
    t3 = signedIn ? history.data ?? [] : [];
    $[5] = history;
    $[6] = signedIn;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const messages = t3;
  const [draft, setDraft] = useState("");
  const scroller = useRef(null);
  const inputRef = useRef(null);
  let t4;
  let t5;
  if ($[8] !== onClose) {
    t4 = () => {
      const onKey = e => {
        if (e.key === "Escape") {
          onClose();
        }
      };
      window.addEventListener("keydown", onKey);
      const lock = isCompactViewport();
      const prevOverflow = document.body.style.overflow;
      if (lock) {
        document.body.style.overflow = "hidden";
      }
      return () => {
        window.removeEventListener("keydown", onKey);
        if (lock) {
          document.body.style.overflow = prevOverflow;
        }
      };
    };
    t5 = [onClose];
    $[8] = onClose;
    $[9] = t4;
    $[10] = t5;
  } else {
    t4 = $[9];
    t5 = $[10];
  }
  useEffect(t4, t5);
  const tail = `${messages.length}:${ask.isPending}:${ask.isError}`;
  const seen = useRef(messages.length);
  let t6;
  if ($[11] !== messages.length) {
    t6 = () => {
      const el = scroller.current;
      if (!el) {
        return;
      }
      const grew = messages.length === seen.current + 1;
      seen.current = messages.length;
      const items = el.querySelectorAll("[data-assistant-role]");
      const last = items[items.length - 1];
      const question = items[items.length - 2];
      if (grew && last?.dataset.assistantRole === "assistant" && question) {
        el.scrollTop = question.offsetTop - 16;
      } else {
        el.scrollTop = el.scrollHeight;
      }
    };
    $[11] = messages.length;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[13] !== tail) {
    t7 = [tail];
    $[13] = tail;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  useEffect(t6, t7);
  let t8;
  let t9;
  if ($[15] !== signedIn) {
    t8 = () => {
      if (signedIn && !isCompactViewport()) {
        inputRef.current?.focus();
      }
    };
    t9 = [signedIn];
    $[15] = signedIn;
    $[16] = t8;
    $[17] = t9;
  } else {
    t8 = $[16];
    t9 = $[17];
  }
  useEffect(t8, t9);
  let t10;
  if ($[18] !== ask) {
    t10 = text => {
      const message = text.trim().slice(0, BUYER_ASSISTANT_MAX_LENGTH);
      if (!message || ask.isPending) {
        return;
      }
      ask.mutate({
        message
      });
      setDraft("");
    };
    $[18] = ask;
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  const send = t10;
  let t11;
  if ($[20] !== ask) {
    t11 = () => {
      if (ask.variables) {
        ask.mutate({
          message: ask.variables.message,
          retry: true
        });
      }
    };
    $[20] = ask;
    $[21] = t11;
  } else {
    t11 = $[21];
  }
  const retry = t11;
  let t12;
  if ($[22] !== onClose) {
    t12 = () => {
      if (isCompactViewport()) {
        onClose();
      }
    };
    $[22] = onClose;
    $[23] = t12;
  } else {
    t12 = $[23];
  }
  const onNavigate = t12;
  let t13;
  if ($[24] !== t) {
    t13 = t("assistant.title");
    $[24] = t;
    $[25] = t13;
  } else {
    t13 = $[25];
  }
  let t14;
  if ($[26] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = cn("fixed inset-0 z-[60] flex h-dvh flex-col overflow-hidden bg-[#F8F8F8]", "lg:inset-auto lg:right-6 lg:bottom-6 lg:h-[min(600px,calc(100dvh-48px))] lg:w-[400px] lg:rounded-[20px] lg:shadow-[0_16px_48px_rgba(0,0,0,0.18)]", "animate-in fade-in-0 slide-in-from-bottom-4 duration-200");
    $[26] = t14;
  } else {
    t14 = $[26];
  }
  let t15;
  if ($[27] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = <span className="grid size-9 shrink-0 place-items-center rounded-full bg-ds-accent/10 text-ds-accent"><Sparkles className="size-[18px]" strokeWidth={1.75} aria-hidden="true" /></span>;
    $[27] = t15;
  } else {
    t15 = $[27];
  }
  let t16;
  if ($[28] !== t) {
    t16 = t("assistant.title");
    $[28] = t;
    $[29] = t16;
  } else {
    t16 = $[29];
  }
  let t17;
  if ($[30] !== t16) {
    t17 = <p className="truncate text-[16px] leading-5 font-semibold text-ds-primary">{t16}</p>;
    $[30] = t16;
    $[31] = t17;
  } else {
    t17 = $[31];
  }
  let t18;
  if ($[32] !== t) {
    t18 = t("assistant.subtitle");
    $[32] = t;
    $[33] = t18;
  } else {
    t18 = $[33];
  }
  let t19;
  if ($[34] !== t18) {
    t19 = <p className="line-clamp-2 text-[12px] leading-4 text-black/50">{t18}</p>;
    $[34] = t18;
    $[35] = t19;
  } else {
    t19 = $[35];
  }
  let t20;
  if ($[36] !== t17 || $[37] !== t19) {
    t20 = <div className="min-w-0 flex-1">{t17}{t19}</div>;
    $[36] = t17;
    $[37] = t19;
    $[38] = t20;
  } else {
    t20 = $[38];
  }
  let t21;
  if ($[39] !== ask || $[40] !== clear || $[41] !== messages.length || $[42] !== signedIn || $[43] !== t) {
    t21 = signedIn && messages.length > 0 && <button type="button" onClick={() => clear.mutate(undefined, {
      onSuccess: () => ask.reset()
    })} disabled={clear.isPending || ask.isPending} className="shrink-0 rounded-[8px] px-1.5 py-1 text-[13px] leading-4 text-black/50 outline-none transition-colors hover:text-ds-primary focus-visible:ring-2 focus-visible:ring-ds-focus disabled:opacity-40" data-testid="assistant-clear">{t("assistant.clear")}</button>;
    $[39] = ask;
    $[40] = clear;
    $[41] = messages.length;
    $[42] = signedIn;
    $[43] = t;
    $[44] = t21;
  } else {
    t21 = $[44];
  }
  let t22;
  if ($[45] !== t) {
    t22 = t("assistant.close");
    $[45] = t;
    $[46] = t22;
  } else {
    t22 = $[46];
  }
  let t23;
  if ($[47] === Symbol.for("react.memo_cache_sentinel")) {
    t23 = <XIcon size={20} />;
    $[47] = t23;
  } else {
    t23 = $[47];
  }
  let t24;
  if ($[48] !== onClose || $[49] !== t22) {
    t24 = <button type="button" onClick={onClose} aria-label={t22} className="grid size-8 shrink-0 place-items-center rounded-full text-ds-primary outline-none transition-colors hover:bg-ds-gray focus-visible:ring-2 focus-visible:ring-ds-focus">{t23}</button>;
    $[48] = onClose;
    $[49] = t22;
    $[50] = t24;
  } else {
    t24 = $[50];
  }
  let t25;
  if ($[51] !== t20 || $[52] !== t21 || $[53] !== t24) {
    t25 = <header className="flex shrink-0 items-center gap-2.5 border-b border-[#F2F2F2] bg-white px-4 pt-[max(12px,env(safe-area-inset-top))] pb-3 lg:pt-3">{t15}{t20}{t21}{t24}</header>;
    $[51] = t20;
    $[52] = t21;
    $[53] = t24;
    $[54] = t25;
  } else {
    t25 = $[54];
  }
  let t26;
  if ($[55] !== ask.isPending || $[56] !== history || $[57] !== messages || $[58] !== onNavigate || $[59] !== send || $[60] !== showSignIn || $[61] !== signedIn || $[62] !== syncWishlist || $[63] !== t) {
    t26 = showSignIn ? <div className="flex flex-col gap-2" data-testid="assistant-sign-in-card"><button type="button" onClick={() => setSigningIn(false)} className="flex items-center gap-1 self-start rounded-[8px] py-1 pr-1.5 text-[13px] leading-4 text-black/50 outline-none transition-colors hover:text-ds-primary focus-visible:ring-2 focus-visible:ring-ds-focus"><ChevronLeft className="size-4" aria-hidden="true" />{t("assistant.back")}</button><SignInCard compact={true} onSignedIn={() => {
        syncWishlist();
        setSigningIn(false);
      }} /></div> : signedIn && history.isPending ? <FeedSkeleton /> : messages.length === 0 ? <EmptyState signedIn={signedIn} onPick={send} disabled={ask.isPending} /> : messages.map(m => <AssistantMessage key={m.id} message={m} onNavigate={onNavigate} />);
    $[55] = ask.isPending;
    $[56] = history;
    $[57] = messages;
    $[58] = onNavigate;
    $[59] = send;
    $[60] = showSignIn;
    $[61] = signedIn;
    $[62] = syncWishlist;
    $[63] = t;
    $[64] = t26;
  } else {
    t26 = $[64];
  }
  let t27;
  if ($[65] !== history || $[66] !== messages.length || $[67] !== signedIn || $[68] !== t) {
    t27 = signedIn && history.isError && messages.length === 0 && <p className="text-center text-[12px] leading-4 text-black/40">{t("assistant.historyError")}</p>;
    $[65] = history;
    $[66] = messages.length;
    $[67] = signedIn;
    $[68] = t;
    $[69] = t27;
  } else {
    t27 = $[69];
  }
  let t28;
  if ($[70] !== ask.isPending) {
    t28 = ask.isPending && <Thinking />;
    $[70] = ask.isPending;
    $[71] = t28;
  } else {
    t28 = $[71];
  }
  let t29;
  if ($[72] !== ask.error || $[73] !== ask.isError || $[74] !== ask.isPending || $[75] !== retry || $[76] !== t) {
    t29 = ask.isError && !ask.isPending && <div className="flex max-w-[85%] flex-col items-start gap-2 rounded-[16px] rounded-bl-[6px] bg-white px-3 py-2.5" role="alert"><p className="text-[14px] leading-[19px] text-ds-primary">{errorText(t, ask.error)}</p><Button variant="gray" size="sm" onClick={retry}>{t("assistant.retry")}</Button></div>;
    $[72] = ask.error;
    $[73] = ask.isError;
    $[74] = ask.isPending;
    $[75] = retry;
    $[76] = t;
    $[77] = t29;
  } else {
    t29 = $[77];
  }
  let t30;
  if ($[78] !== clear.isError || $[79] !== t) {
    t30 = clear.isError && <p className="text-center text-[12px] leading-4 text-ds-error">{t("assistant.clearError")}</p>;
    $[78] = clear.isError;
    $[79] = t;
    $[80] = t30;
  } else {
    t30 = $[80];
  }
  let t31;
  if ($[81] !== t26 || $[82] !== t27 || $[83] !== t28 || $[84] !== t29 || $[85] !== t30) {
    t31 = <div ref={scroller} className="relative flex min-h-0 flex-1 flex-col gap-3 overflow-x-hidden overflow-y-auto overscroll-contain px-4 py-4" data-testid="assistant-feed">{t26}{t27}{t28}{t29}{t30}</div>;
    $[81] = t26;
    $[82] = t27;
    $[83] = t28;
    $[84] = t29;
    $[85] = t30;
    $[86] = t31;
  } else {
    t31 = $[86];
  }
  let t32;
  if ($[87] !== ask.isPending || $[88] !== draft || $[89] !== send || $[90] !== showSignIn || $[91] !== signedIn || $[92] !== t) {
    t32 = signedIn ? <Composer inputRef={inputRef} value={draft} onChange={setDraft} onSend={() => send(draft)} busy={ask.isPending} /> : showSignIn ? null : <div className="flex shrink-0 flex-col items-stretch gap-3 border-t border-[#F2F2F2] bg-white px-4 pt-4 pb-[max(16px,env(safe-area-inset-bottom))] lg:pb-4"><p className="text-center text-[14px] leading-[19px] text-ds-primary">{t("assistant.signInHint")}</p><Button variant="dark" size="md" onClick={() => setSigningIn(true)} data-testid="assistant-sign-in">{t("assistant.signIn")}</Button></div>;
    $[87] = ask.isPending;
    $[88] = draft;
    $[89] = send;
    $[90] = showSignIn;
    $[91] = signedIn;
    $[92] = t;
    $[93] = t32;
  } else {
    t32 = $[93];
  }
  let t33;
  if ($[94] !== t13 || $[95] !== t25 || $[96] !== t31 || $[97] !== t32) {
    t33 = <div role="dialog" aria-modal="false" aria-label={t13} data-testid="assistant-panel" className={t14}>{t25}{t31}{t32}</div>;
    $[94] = t13;
    $[95] = t25;
    $[96] = t31;
    $[97] = t32;
    $[98] = t33;
  } else {
    t33 = $[98];
  }
  return t33;
}
function _temp5() {}
function _temp4(s_2) {
  return s_2.syncFromServer;
}
function _temp3(s_1) {
  return s_1.hydrateMe;
}
function _temp2(s_0) {
  return s_0.user;
}
function _temp(s) {
  return s.token;
}
function EmptyState(t0) {
  const $ = _c(20);
  const {
    signedIn,
    onPick,
    disabled
  } = t0;
  const {
    t
  } = useTranslation();
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <span className="grid size-14 place-items-center rounded-full bg-white text-ds-accent shadow-[0_6px_16px_rgba(0,0,0,0.06)]"><Sparkles className="size-7" strokeWidth={1.5} aria-hidden="true" /></span>;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== t) {
    t2 = t("assistant.emptyTitle");
    $[1] = t;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== t2) {
    t3 = <p className="text-[16px] leading-5 font-semibold text-ds-primary">{t2}</p>;
    $[3] = t2;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== t) {
    t4 = t("assistant.emptyHint");
    $[5] = t;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== t4) {
    t5 = <p className="mx-auto max-w-[300px] text-[13px] leading-[18px] text-black/50">{t4}</p>;
    $[7] = t4;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== t3 || $[10] !== t5) {
    t6 = <div className="flex flex-col gap-1">{t3}{t5}</div>;
    $[9] = t3;
    $[10] = t5;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== disabled || $[13] !== onPick || $[14] !== signedIn || $[15] !== t) {
    t7 = signedIn && <div className="flex w-full flex-col items-stretch gap-2">{SUGGESTIONS.map(key => <button key={key} type="button" disabled={disabled} onClick={() => onPick(t(`assistant.suggestions.${key}`))} className="rounded-[14px] border-[0.5px] border-ds-field-line bg-white px-3.5 py-2.5 text-left text-[14px] leading-[18px] text-ds-primary outline-none transition-colors hover:border-ds-accent/50 hover:bg-ds-accent/5 focus-visible:ring-2 focus-visible:ring-ds-focus disabled:opacity-50" data-testid="assistant-suggestion">{t(`assistant.suggestions.${key}`)}</button>)}</div>;
    $[12] = disabled;
    $[13] = onPick;
    $[14] = signedIn;
    $[15] = t;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  let t8;
  if ($[17] !== t6 || $[18] !== t7) {
    t8 = <div className="my-auto flex flex-col items-center gap-4 py-4 text-center">{t1}{t6}{t7}</div>;
    $[17] = t6;
    $[18] = t7;
    $[19] = t8;
  } else {
    t8 = $[19];
  }
  return t8;
}

// Ответ идёт 10-40 сек: три точки и «Думаю…», через 12 сек — что ещё не всё.
function Thinking() {
  const $ = _c(8);
  const {
    t
  } = useTranslation();
  const [slow, setSlow] = useState(false);
  let t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = () => {
      const id = window.setTimeout(() => setSlow(true), 12000);
      return () => window.clearTimeout(id);
    };
    t1 = [];
    $[0] = t0;
    $[1] = t1;
  } else {
    t0 = $[0];
    t1 = $[1];
  }
  useEffect(t0, t1);
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <span className="flex gap-1" aria-hidden="true">{[0, 150, 300].map(_temp6)}</span>;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== slow || $[4] !== t) {
    t3 = slow ? t("assistant.thinkingSlow") : t("assistant.thinking");
    $[3] = slow;
    $[4] = t;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== t3) {
    t4 = <div className="flex max-w-[85%] items-center gap-2 self-start rounded-[16px] rounded-bl-[6px] bg-white px-3 py-2.5" aria-live="polite" data-testid="assistant-thinking">{t2}<span className="text-[14px] leading-[19px] text-black/50">{t3}</span></div>;
    $[6] = t3;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  return t4;
}
function _temp6(delay) {
  return <span key={delay} className="size-1.5 animate-bounce rounded-full bg-ds-accent/70" style={{
    animationDelay: `${delay}ms`
  }} />;
}
function FeedSkeleton() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <div className="flex flex-col gap-3" aria-hidden="true"><span className="h-10 w-2/3 self-end animate-pulse rounded-[16px] bg-[#EFEFEF]" /><span className="h-20 w-4/5 animate-pulse rounded-[16px] bg-white" /><span className="h-10 w-1/2 self-end animate-pulse rounded-[16px] bg-[#EFEFEF]" /></div>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}

// Поле ввода: Enter отправляет, Shift+Enter — перенос строки. Поле растёт по
// тексту до ~5 строк; шрифт 16px, чтобы iOS не увеличивал страницу при фокусе.
function Composer(t0) {
  const $ = _c(33);
  const {
    inputRef,
    value,
    onChange,
    onSend,
    busy
  } = t0;
  const {
    t
  } = useTranslation();
  const canSend = value.trim().length > 0 && !busy;
  const nearLimit = value.length > BUYER_ASSISTANT_MAX_LENGTH - 200;
  let t1;
  if ($[0] !== onChange) {
    t1 = e => onChange(e.target.value);
    $[0] = onChange;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== canSend || $[3] !== onSend) {
    t2 = e_0 => {
      if (e_0.key === "Enter" && !e_0.shiftKey && !e_0.nativeEvent.isComposing) {
        e_0.preventDefault();
        if (canSend) {
          onSend();
        }
      }
    };
    $[2] = canSend;
    $[3] = onSend;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== t) {
    t3 = t("assistant.placeholder");
    $[5] = t;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== t) {
    t4 = t("assistant.inputLabel");
    $[7] = t;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== inputRef || $[10] !== t1 || $[11] !== t2 || $[12] !== t3 || $[13] !== t4 || $[14] !== value) {
    t5 = <textarea ref={inputRef} value={value} onChange={t1} onKeyDown={t2} rows={1} maxLength={BUYER_ASSISTANT_MAX_LENGTH} placeholder={t3} aria-label={t4} className="field-sizing-content max-h-[120px] min-h-10 min-w-0 flex-1 resize-none rounded-[20px] border border-transparent bg-ds-field px-4 py-[9px] text-[16px] leading-[20px] text-ds-primary outline-none transition-[border-color] placeholder:text-ds-placeholder focus-visible:border-ds-focus lg:text-[14px]" data-testid="assistant-input" />;
    $[9] = inputRef;
    $[10] = t1;
    $[11] = t2;
    $[12] = t3;
    $[13] = t4;
    $[14] = value;
    $[15] = t5;
  } else {
    t5 = $[15];
  }
  const t6 = !canSend;
  let t7;
  if ($[16] !== t) {
    t7 = t("assistant.send");
    $[16] = t;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  const t8 = busy || undefined;
  let t9;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <SendIcon size={20} />;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  let t10;
  if ($[19] !== onSend || $[20] !== t6 || $[21] !== t7 || $[22] !== t8) {
    t10 = <button type="button" onClick={onSend} disabled={t6} aria-label={t7} aria-busy={t8} className="grid size-10 shrink-0 place-items-center rounded-full bg-ds-accent text-white outline-none transition-colors hover:bg-ds-accent/90 focus-visible:ring-2 focus-visible:ring-ds-focus focus-visible:ring-offset-2 disabled:bg-ds-gray disabled:text-ds-disabled" data-testid="assistant-send">{t9}</button>;
    $[19] = onSend;
    $[20] = t6;
    $[21] = t7;
    $[22] = t8;
    $[23] = t10;
  } else {
    t10 = $[23];
  }
  let t11;
  if ($[24] !== t10 || $[25] !== t5) {
    t11 = <div className="flex items-end gap-2">{t5}{t10}</div>;
    $[24] = t10;
    $[25] = t5;
    $[26] = t11;
  } else {
    t11 = $[26];
  }
  let t12;
  if ($[27] !== nearLimit || $[28] !== value.length) {
    t12 = nearLimit && <p className="mt-1 px-2 text-right text-[11px] leading-[14px] text-black/40">{value.length} / {BUYER_ASSISTANT_MAX_LENGTH}</p>;
    $[27] = nearLimit;
    $[28] = value.length;
    $[29] = t12;
  } else {
    t12 = $[29];
  }
  let t13;
  if ($[30] !== t11 || $[31] !== t12) {
    t13 = <div className="shrink-0 border-t border-[#F2F2F2] bg-white px-3 pt-2.5 pb-[max(10px,env(safe-area-inset-bottom))] lg:pb-2.5">{t11}{t12}</div>;
    $[30] = t11;
    $[31] = t12;
    $[32] = t13;
  } else {
    t13 = $[32];
  }
  return t13;
}